import { useEffect, useMemo, useState } from "react";
import { ArrowLeft, Home, Search, Star } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { getSchoolTeachers } from "../../lib/TeachersApi";
import { useAuth } from "../../context/AuthProvider";

const TeachersPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [schools, setSchools] = useState([]);
  const [selectedSchoolId, setSelectedSchoolId] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  let storedUser = {};
  try {
    storedUser = JSON.parse(localStorage.getItem("user") || "{}");
  } catch {
    storedUser = {};
  }

  const role = String(
    user?.roleName || user?.role || storedUser?.roleName || storedUser?.role || ""
  ).toLowerCase();

  useEffect(() => {
    if (role && role !== "student") {
      navigate("/live-classes", { replace: true });
    }
  }, [navigate, role]);

  useEffect(() => {
    const loadTeachers = async () => {
      if (role && role !== "student") return;

      try {
        const storedUser = JSON.parse(localStorage.getItem("user") || "null");
        const userId = storedUser?.uuid || storedUser?.result?.uuid || storedUser?.id || localStorage.getItem("userId");

        if (!userId) throw new Error("Student ID is missing. Please log in again.");

        const data = await getSchoolTeachers(userId);
        if (!data?.success) throw new Error(data?.message || "Unable to load teachers");

        const school = {
          id: data.schoolId,
          name: `School ${data.schoolId}`,
          teachers: (data.teachers || []).map((teacher) => ({
            id: teacher.teacherId,
            name: teacher.fullName || `${teacher.firstName || ""} ${teacher.lastName || ""}`.trim() || "Teacher",
            initials: `${teacher.firstName?.[0] || ""}${teacher.lastName?.[0] || ""}`.toUpperCase() || "T",
            photo: teacher.photo || teacher.profileUrl,
            qualification: teacher.aboutUs || "Teacher",
            experience: teacher.email ? `Contact: ${teacher.email}` : "Available for your school classes.",
            subject: teacher.isInstructor ? "Instructor" : "Teacher",
            classes: teacher.isK12 ? "K–12" : "School",
          })),
        };

        setSchools([school]);
        setSelectedSchoolId(String(school.id));
      } catch (loadError) {
        setError(loadError.message);
      } finally {
        setLoading(false);
      }
    };

    loadTeachers();
  }, [role]);

  const selectedSchool = schools.find(
    (school) => String(school.id) === selectedSchoolId
  );

  const teachers = useMemo(() => {
    const query = search.trim().toLowerCase();
    return (selectedSchool?.teachers || []).filter((teacher) => {
      if (!query) return true;
      return [teacher.name, teacher.subject, teacher.qualification]
        .join(" ")
        .toLowerCase()
        .includes(query);
    });
  }, [selectedSchool, search]);

  return (
    <div className="min-h-full bg-[#f7f3ea] px-[14px] py-[22px] font-sans text-[#211b4e] sm:px-[30px] sm:py-7">
      <div className="mx-auto w-full max-w-[1120px]">
        <header className="mb-[22px] flex items-start justify-between gap-6 sm:items-end">
          <div>
            <p className="mb-2 text-[11px] font-bold uppercase tracking-[.12em] text-[#8b84a3]">Student learning space</p>
            <h1 className="font-sans text-[clamp(28px,4vw,43px)] font-extrabold leading-[1.05] tracking-[-.04em] text-[#3f35c7]">Know your teachers</h1>
            <p className="mt-[9px] text-[13px] text-[#716a88]">
              Meet the educators supporting your learning journey.
            </p>
          </div>
          <div className="flex gap-1.5 sm:gap-2">
            <button className="grid h-9 w-9 place-items-center rounded-full border border-[#ddd4c4] bg-white text-[#3f35c7] transition hover:bg-[#3f35c7] hover:text-white sm:h-[42px] sm:w-[42px]" type="button" onClick={() => navigate("/live-classes")} aria-label="Home">
              <Home size={18} />
            </button>
            <button className="grid h-9 w-9 place-items-center rounded-full border border-[#ddd4c4] bg-white text-[#3f35c7] transition hover:bg-[#3f35c7] hover:text-white sm:h-[42px] sm:w-[42px]" type="button" onClick={() => navigate(-1)} aria-label="Go back">
              <ArrowLeft size={18} />
            </button>
          </div>
        </header>

        <section className="mb-[18px] grid gap-3 rounded-2xl border border-[#e7dfd0] bg-white p-4 sm:flex" aria-label="Teacher filters">
          <label className="grid gap-1.5 text-[11px] font-bold text-[#716a88]">
            <span>School</span>
            <select className="h-[38px] min-w-[210px] rounded-[9px] border border-[#ded6c8] bg-white px-2.5 text-[#211b4e] outline-none focus:border-[#3f35c7]" value={selectedSchoolId} onChange={(event) => setSelectedSchoolId(event.target.value)}>
              {schools.map((school) => (
                <option key={school.id} value={school.id}>{school.name}</option>
              ))}
            </select>
          </label>
          <label className="grid max-w-none flex-1 gap-1.5 text-[11px] font-bold text-[#716a88] sm:max-w-[360px]">
            <span>Search teachers</span>
            <div className="flex h-[38px] items-center gap-2 rounded-[9px] border border-[#ded6c8] px-[11px] text-[#8b84a3]">
              <Search size={17} />
              <input className="min-w-0 w-full border-0 bg-transparent text-[#211b4e] outline-none placeholder:text-[#8b84a3]" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Name or subject" />
            </div>
          </label>
        </section>

        {loading && <div className="rounded-2xl bg-white p-9 text-center text-[#716a88]">Loading teachers…</div>}
        {error && <div className="rounded-2xl bg-white p-9 text-center text-[#a8442b]">{error}</div>}
        {!loading && !error && !teachers.length && <div className="rounded-2xl bg-white p-9 text-center text-[#716a88]">No teachers found.</div>}

        <div className="grid gap-[18px]">
          {teachers.map((teacher) => (
            <article className="flex min-h-[142px] items-start gap-[14px] rounded-[18px] bg-[#3f35c7] p-[18px] text-white shadow-[0_10px_24px_rgba(63,53,199,.09)] sm:items-center sm:gap-[22px] sm:p-[26px]" key={teacher.id}>
              {teacher.photo ? (
                <img className="h-[58px] w-[58px] shrink-0 rounded-full object-cover sm:h-24 sm:w-24" src={teacher.photo} alt={teacher.name} />
              ) : (
                <div className="grid h-[58px] w-[58px] shrink-0 place-items-center rounded-full bg-[#645bd2] font-sans text-lg font-extrabold sm:h-24 sm:w-24 sm:text-[29px]">{teacher.initials}</div>
              )}
              <div className="min-w-0">
                <h2 className="mb-[5px] font-sans text-lg font-extrabold leading-tight tracking-[-.04em] sm:text-[23px]">{teacher.name}</h2>
                <p className="text-xs leading-[1.5] text-[#f1efff] sm:text-sm">{teacher.qualification} {teacher.experience}</p>
                <div className="mt-[14px] flex flex-wrap items-center gap-[7px] text-[10px] sm:gap-x-4 sm:gap-y-2 sm:text-xs">
                  <span className="inline-flex items-center gap-[5px] rounded-full bg-[#655bd4] px-[9px] py-[7px] font-bold sm:px-[13px] sm:py-2">{teacher.subject} · classes {teacher.classes}</span>
                  {teacher.rating && <span className="inline-flex items-center gap-[5px] rounded-full bg-[#655bd4] px-[9px] py-[7px] font-bold sm:px-[13px] sm:py-2"><Star size={14} fill="currentColor" /> {teacher.rating} from {teacher.ratingsCount} ratings</span>}
                  <button className="cursor-pointer border-0 bg-transparent p-0 font-inherit text-white underline" type="button" onClick={() => window.alert(`${teacher.name}\n\n${teacher.qualification} ${teacher.experience}`)}>Read more</button>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </div>
  );
};

export default TeachersPage;
